if (!customElements.get('td-header-panel')) {
  class TDHeaderPanel extends HTMLElement {
    static TILE_CACHE_PREFIX = 'td-tile:';
    static SAMPLE_SIZE = 8;
    static SAMPLE_SPREAD_LIMIT = 28;

    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.activeKey = this.querySelector('[data-td-panel-content][data-td-active="true"]')?.dataset.tdPanelContentKey;

      this.addEventListener('pointerover', this.handleIntent, { signal });
      this.addEventListener('focusin', this.handleIntent, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, { signal });

      this.tintTiles();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      cancelAnimationFrame(this.revealFrame);
    }

    handleIntent = (event) => {
      const trigger = event.target.closest?.('[data-td-panel-nav]');
      if (!trigger || !this.contains(trigger)) return;

      this.activate(trigger.dataset.tdPanelNavKey);
    };

    handleBlockSelect = (event) => {
      const group = event.target?.closest?.('[data-td-panel-content]');
      if (!group || !this.contains(group)) return;

      this.activate(group.dataset.tdPanelContentKey);
      this.setEditorOpen(true);
    };

    handleBlockDeselect = (event) => {
      const group = event.target?.closest?.('[data-td-panel-content]');
      if (!group || !this.contains(group)) return;

      this.setEditorOpen(false);
    };

    setEditorOpen(isOpen) {
      const item = this.closest('.menu-list__list-item');
      const trigger = item?.querySelector('[ref="menuitem"]');
      if (!item || !trigger) return;

      this.dataset.tdEditorOpen = isOpen ? 'true' : 'false';
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      item.dispatchEvent(new PointerEvent(isOpen ? 'pointerenter' : 'pointerleave', { bubbles: false }));
    }

    activate(key) {
      if (!key || key === this.activeKey) return;
      this.activeKey = key;

      for (const link of this.querySelectorAll('[data-td-panel-nav]')) {
        const isActive = link.dataset.tdPanelNavKey === key;
        link.classList.toggle('td-header-panel__nav-link--active', isActive);
        link.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }

      const groups = this.querySelectorAll('[data-td-panel-content]');
      let activeGroup = null;

      for (const group of groups) {
        const isActive = group.dataset.tdPanelContentKey === key;
        group.dataset.tdActive = isActive ? 'true' : 'false';
        if (isActive) {
          group.setAttribute('data-td-revealing', 'true');
          activeGroup = group;
        } else {
          group.removeAttribute('data-td-revealing');
        }
      }

      if (activeGroup) activeGroup.getBoundingClientRect();

      cancelAnimationFrame(this.revealFrame);
      this.revealFrame = requestAnimationFrame(() => {
        for (const group of groups) group.removeAttribute('data-td-revealing');
      });
    }

    tintTiles() {
      for (const tile of this.querySelectorAll('[data-td-tile]')) {
        const image = tile.querySelector('img');
        const source = image?.currentSrc || image?.src;
        if (!source) continue;

        const cached = this.readCache(source);
        if (cached) {
          tile.style.setProperty('--td-tile-bg', cached);
          continue;
        }

        this.sampleEdgeColor(source).then((color) => {
          if (!color) return;
          this.writeCache(source, color);
          tile.style.setProperty('--td-tile-bg', color);
        });
      }
    }

    readCache(source) {
      try {
        return sessionStorage.getItem(TDHeaderPanel.TILE_CACHE_PREFIX + source);
      } catch {
        return null;
      }
    }

    writeCache(source, color) {
      try {
        sessionStorage.setItem(TDHeaderPanel.TILE_CACHE_PREFIX + source, color);
      } catch {
        // Private browsing or a full quota: the tile keeps its default.
      }
    }

    sampleEdgeColor(source) {
      return new Promise((resolve) => {
        const probe = new Image();
        probe.crossOrigin = 'anonymous';

        probe.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = TDHeaderPanel.SAMPLE_SIZE;
            canvas.height = TDHeaderPanel.SAMPLE_SIZE;

            const context = canvas.getContext('2d', { willReadFrequently: true });
            context.drawImage(probe, 0, 0, TDHeaderPanel.SAMPLE_SIZE, TDHeaderPanel.SAMPLE_SIZE);

            const { data } = context.getImageData(0, 0, TDHeaderPanel.SAMPLE_SIZE, TDHeaderPanel.SAMPLE_SIZE);
            const last = TDHeaderPanel.SAMPLE_SIZE - 1;
            const mid = Math.floor(TDHeaderPanel.SAMPLE_SIZE / 2);
            const ring = [[0, 0], [last, 0], [0, last], [last, last], [mid, 0], [mid, last], [0, mid], [last, mid]];
            const samples = [];

            for (const [x, y] of ring) {
              const index = (y * TDHeaderPanel.SAMPLE_SIZE + x) * 4;
              if (data[index + 3] < 250) continue;
              samples.push([data[index], data[index + 1], data[index + 2]]);
            }

            if (samples.length < 4) return resolve(null);

            const mean = samples
              .reduce((total, channel) => [total[0] + channel[0], total[1] + channel[1], total[2] + channel[2]], [0, 0, 0])
              .map((total) => Math.round(total / samples.length));
            const spread = Math.max(...samples.flatMap((channel) => channel.map((value, i) => Math.abs(value - mean[i]))));

            if (spread > TDHeaderPanel.SAMPLE_SPREAD_LIMIT) return resolve(null);
            resolve(`rgb(${mean[0]} ${mean[1]} ${mean[2]})`);
          } catch {
            resolve(null);
          }
        };

        probe.onerror = () => resolve(null);
        probe.src = source;
      });
    }
  }

  customElements.define('td-header-panel', TDHeaderPanel);
}

if (!customElements.get('td-header-bar')) {
  class TDHeaderBar extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener('click', this.handleKlaviyoTrigger, { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleKlaviyoTrigger = (event) => {
      const trigger = event.target.closest?.('[data-td-klaviyo-form-id]');
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', trigger.dataset.tdKlaviyoFormId]);
    };
  }

  customElements.define('td-header-bar', TDHeaderBar);
}
